import { Component, OnInit, OnDestroy, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'app/shared/services/core';
import { UsuarioService } from 'app/shared/services/edduca-imob/usuario/usuario.service';
import { TokenVO } from 'app/shared/models/core/security/token-vo';
import { PerfilEnum } from 'app/shared/models/core/security/perfil.enum';

export interface TourStep {
  id: string;
  title: string;
  description: string;
  menuKeyword?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  action?: 'welcome' | 'highlight' | 'finish';
  icon?: string;
}

// Configuração dos menus do tour
interface MenuTourConfig {
  id: string;
  title: string;
  description: string;
  menuKeyword: string;
  icon: string;
}

const MENU_CONFIGS: { [key: string]: MenuTourConfig } = {
  gestao: {
    id: 'gestao',
    title: '📊 Gestão',
    description: 'Acompanhe o desempenho da sua equipe, veja estatísticas e gerencie o progresso de todos os colaboradores da imobiliária.',
    menuKeyword: 'gestão',
    icon: 'dashboard'
  },
  academiaEdduca: {
    id: 'academia-edduca',
    title: '📚 Academia Edduca',
    description: 'Aqui você encontra todas as trilhas de aprendizado e cursos disponíveis. É o coração da plataforma para sua evolução profissional!',
    menuKeyword: 'academia',
    icon: 'school'
  },
  aulasAoVivo: {
    id: 'aulas-ao-vivo',
    title: '🎥 Aulas ao Vivo',
    description: 'Participe de aulas ao vivo com especialistas do mercado imobiliário. Interaja em tempo real e tire suas dúvidas!',
    menuKeyword: 'aulas ao vivo',
    icon: 'videocam'
  },
  edducai: {
    id: 'edducai',
    title: '🤖 EdducAI',
    description: 'Conheça nossa inteligência artificial! Tire dúvidas, peça resumos e tenha um assistente disponível 24h para ajudar nos seus estudos.',
    menuKeyword: 'edducai',
    icon: 'smart_toy'
  },
  ranking: {
    id: 'ranking',
    title: '🏆 Ranking',
    description: 'Veja sua posição no ranking! Compare seu desempenho com outros profissionais e motive-se a subir cada vez mais.',
    menuKeyword: 'ranking',
    icon: 'leaderboard'
  },
  badges: {
    id: 'badges',
    title: '🏅 Badges',
    description: 'Conquiste badges ao completar desafios e marcos importantes. Mostre suas conquistas e seja reconhecido pelo seu esforço!',
    menuKeyword: 'badge',
    icon: 'military_tech'
  },
  feedbacks: {
    id: 'feedbacks',
    title: '💬 Feedbacks',
    description: 'Aqui você pode enviar feedbacks para seus colegas e ler os feedbacks recebidos. O crescimento profissional é uma via de mão dupla!',
    menuKeyword: 'feedback',
    icon: 'rate_review'
  },
  newsletter: {
    id: 'newsletter',
    title: '📰 Newsletter',
    description: 'Fique por dentro das novidades do mercado imobiliário! Receba conteúdos exclusivos e atualizações importantes.',
    menuKeyword: 'newsletter',
    icon: 'newspaper'
  },
  certificados: {
    id: 'certificados',
    title: '🎓 Certificados',
    description: 'Acesse todos os seus certificados conquistados. Baixe e compartilhe suas conquistas nas redes sociais!',
    menuKeyword: 'certificado',
    icon: 'workspace_premium'
  },
  comunicados: {
    id: 'comunicados',
    title: '📢 Comunicados',
    description: 'Clique no ícone do megafone para ver os comunicados importantes da plataforma e da sua imobiliária. Fique sempre atualizado!',
    menuKeyword: 'comunicado',
    icon: 'campaign'
  },
  meuPerfil: {
    id: 'meu-perfil',
    title: '👤 Meu Perfil',
    description: 'Clique no ícone do usuário para acessar seu perfil. Lá você pode personalizar suas informações e acompanhar seu progresso.',
    menuKeyword: 'meu perfil',
    icon: 'person'
  },
  minhaImobiliaria: {
    id: 'minha-imobiliaria',
    title: '🏢 Minha Imobiliária',
    description: 'No menu do usuário, acesse "Minha Imobiliária" para gerenciar suas informações e ver detalhes sobre o plano contratado.',
    menuKeyword: 'minha imobiliária',
    icon: 'business'
  },
  usuarios: {
    id: 'usuarios',
    title: '👥 Usuários',
    description: 'No menu do usuário, acesse "Usuários" para gerenciar os colaboradores da sua imobiliária e adicionar novos membros.',
    menuKeyword: 'usuário',
    icon: 'groups'
  }
};

@Component({
  selector: 'app-onboarding-tour',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './onboarding-tour.component.html',
  styleUrl: './onboarding-tour.component.scss',
  host: {
    'style': 'position: absolute; top: 0; left: 0; width: 0; height: 0; overflow: visible; pointer-events: none;'
  }
})
export class OnboardingTourComponent implements OnInit, OnDestroy {
  isVisible = false;
  isLoading = false; // Controla quando o tooltip está em transição
  currentStepIndex = 0;
  highlightRect: DOMRect | null = null;
  tooltipPosition = { top: 0, left: 0 };
  arrowDirection: 'top' | 'bottom' | 'left' | 'right' = 'bottom';

  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;

  steps: TourStep[] = [];

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private usuarioService: UsuarioService
  ) { }

  ngOnInit(): void {
    this.setupMutationObserver();
    this.setupResizeObserver();
  }

  ngOnDestroy(): void {
    this.cleanupObservers();
    this.removeHighlight();
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isVisible && this.currentStep?.menuKeyword) {
      this.positionTooltip();
    }
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if (this.isVisible && this.currentStep?.menuKeyword) {
      this.positionTooltip();
    }
  }

  get currentStep(): TourStep {
    return this.steps[this.currentStepIndex];
  }

  get isFirstStep(): boolean {
    return this.currentStepIndex === 0;
  }

  get isLastStep(): boolean {
    return this.currentStepIndex === this.steps.length - 1;
  }

  get progressPercent(): number {
    return ((this.currentStepIndex + 1) / this.steps.length) * 100;
  }

  /**
   * Verifica se o tour já foi completado (via token)
   */
  isTourCompleted(): boolean {
    const currentUser = this.authService.getUserInToken();
    return currentUser?.onboardingConcluido === 'True';
  }

  /**
   * Gera os steps do tour baseado no perfil e plano do usuário
   */
  private generateSteps(): void {
    const user = this.authService.getUserInToken();
    if (!user) {
      this.steps = this.getDefaultSteps();
      return;
    }

    const perfilId = user.perfilId;
    const planoId = user.planoId || 1;

    // Step de boas-vindas
    const welcomeStep: TourStep = {
      id: 'welcome',
      title: '🎉 Bem-vindo à Edduca Imob!',
      description: 'Estamos muito felizes em ter você aqui! Vamos fazer um rápido tour pela plataforma para você conhecer os principais recursos e começar sua jornada de aprendizado.',
      action: 'welcome'
    };

    // Step de finalização
    const finishStep: TourStep = {
      id: 'finish',
      title: '✅ Tudo pronto!',
      description: 'Agora você já conhece todos os recursos da plataforma. Explore, aprenda e conquiste novos conhecimentos! Caso tenha dúvidas, estamos sempre aqui para ajudar. Bons estudos! 🚀',
      action: 'finish'
    };

    // Menus baseados no perfil e plano
    let menuKeys: string[] = [];

    if (perfilId === PerfilEnum.ADM_IMOBILIARIA) {
      if (planoId === 1) {
        // Plano 1 - ADM Imobiliária
        menuKeys = [
          'gestao', 'academiaEdduca', 'aulasAoVivo', 'edducai', 'ranking',
          'feedbacks', 'certificados', 'comunicados', 'meuPerfil', 'minhaImobiliaria', 'usuarios'
        ];
      } else {
        // Plano 2+ - ADM Imobiliária
        menuKeys = [
          'gestao', 'academiaEdduca', 'aulasAoVivo', 'edducai', 'ranking', 'badges',
          'feedbacks', 'newsletter', 'certificados', 'comunicados', 'meuPerfil', 'minhaImobiliaria', 'usuarios'
        ];
      }
    } else if (perfilId === PerfilEnum.USUARIO_IMOBILIARIA) {
      if (planoId === 1) {
        // Plano 1 - Usuário Imobiliária
        menuKeys = [
          'academiaEdduca', 'aulasAoVivo', 'edducai', 'ranking',
          'feedbacks', 'certificados', 'comunicados', 'meuPerfil'
        ];
      } else {
        // Plano 2+ - Usuário Imobiliária
        menuKeys = [
          'academiaEdduca', 'aulasAoVivo', 'edducai', 'ranking', 'badges',
          'feedbacks', 'newsletter', 'certificados', 'comunicados', 'meuPerfil'
        ];
      }
    } else {
      // Outros perfis (ADM Sistema, Parceiro) - tour básico
      menuKeys = [
        'gestao', 'academiaEdduca', 'aulasAoVivo', 'edducai', 'ranking', 'badges',
        'feedbacks', 'newsletter', 'certificados', 'comunicados', 'meuPerfil', 'minhaImobiliaria', 'usuarios'
      ];
    }

    // Gerar steps dos menus
    const menuSteps: TourStep[] = menuKeys.map(key => {
      const config = MENU_CONFIGS[key];
      return {
        id: config.id,
        title: config.title,
        description: config.description,
        menuKeyword: config.menuKeyword,
        position: 'right' as const,
        action: 'highlight' as const,
        icon: config.icon
      };
    });

    this.steps = [welcomeStep, ...menuSteps, finishStep];
  }

  private getDefaultSteps(): TourStep[] {
    return [
      {
        id: 'welcome',
        title: '🎉 Bem-vindo à Edduca Imob!',
        description: 'Estamos muito felizes em ter você aqui! Explore a plataforma e comece sua jornada de aprendizado.',
        action: 'welcome'
      },
      {
        id: 'finish',
        title: '✅ Tudo pronto!',
        description: 'Explore, aprenda e conquiste novos conhecimentos!',
        action: 'finish'
      }
    ];
  }

  /**
   * Inicia o tour
   */
  startTour(): void {
    if (this.isTourCompleted()) {
      return;
    }

    this.generateSteps();
    this.currentStepIndex = 0;
    this.isVisible = true;
    this.showStep();
  }

  /**
   * Exibe o step atual
   */
  private showStep(): void {
    const step = this.currentStep;

    if (step.action === 'highlight' && step.menuKeyword) {
      // Oculta o tooltip durante a transição
      this.isLoading = true;
      this.cdr.detectChanges();
      
      // Destaca o menu e só depois mostra o tooltip
      this.highlightMenu(step.menuKeyword!);
    } else {
      this.isLoading = false;
      this.highlightRect = null;
      this.centerTooltip();
      this.cdr.detectChanges();
    }
  }

  /**
   * Destaca um menu pelo keyword
   */
  private highlightMenu(keyword: string): void {
    const element = this.findMenuByKeyword(keyword);

    if (element) {
      // Verifica se precisa de scroll
      const needsScroll = this.needsScrollToElement(element);
      
      if (needsScroll) {
        // Faz scroll e aguarda completar
        this.scrollToElement(element);
        setTimeout(() => {
          this.applyHighlight(element);
        }, 300);
      } else {
        // Não precisa scroll, aplica highlight imediatamente
        this.applyHighlight(element);
      }
    } else {
      console.warn('Menu não encontrado:', keyword);
      this.isLoading = false;
      this.highlightRect = null;
      this.centerTooltip();
      this.cdr.detectChanges();
    }
  }

  /**
   * Aplica o highlight no elemento e mostra o tooltip
   */
  private applyHighlight(element: Element): void {
    const rect = element.getBoundingClientRect();
    this.highlightRect = rect;
    this.positionTooltip();
    element.classList.add('tour-highlight-element');
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  /**
   * Verifica se o elemento precisa de scroll para ficar visível
   */
  private needsScrollToElement(element: Element): boolean {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Elementos da navbar não precisam de scroll
    if (rect.top < 80 && rect.left > window.innerWidth / 2) {
      return false;
    }

    const isAboveViewport = rect.top < 100;
    const isBelowViewport = rect.bottom > viewportHeight - 100;

    return isAboveViewport || isBelowViewport;
  }

  /**
   * Faz scroll suave para o elemento ficar visível na tela
   */
  private scrollToElement(element: Element): void {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Se é elemento da navbar (topo, lado direito), não precisa scroll
    if (rect.top < 80 && rect.left > window.innerWidth / 2) {
      return;
    }

    // Verifica se o elemento está fora da área visível
    const isAboveViewport = rect.top < 100;
    const isBelowViewport = rect.bottom > viewportHeight - 100;

    if (isAboveViewport || isBelowViewport) {
      // Procura o container de scroll da navegação
      const navContent = document.querySelector('.fuse-vertical-navigation-content');

      if (navContent) {
        // Calcula quanto precisa scrollar
        const navRect = navContent.getBoundingClientRect();
        const elementRelativeTop = rect.top - navRect.top + navContent.scrollTop;

        // Posiciona o elemento no centro da área visível da navegação
        const targetScroll = elementRelativeTop - (navContent.clientHeight / 2) + (rect.height / 2);

        navContent.scrollTo({
          top: Math.max(0, targetScroll),
          behavior: 'smooth'
        });
      } else {
        // Fallback: usa scrollIntoView
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  }

  /**
   * Encontra um menu pelo keyword no texto
   */
  private findMenuByKeyword(keyword: string): Element | null {
    const normalizedKeyword = keyword.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    // Se for comunicados, busca o botão de comunicados na navbar
    if (normalizedKeyword.includes('comunicado')) {
      const comunicadosButton = document.querySelector('button[matTooltip="Comunicados"]');
      if (comunicadosButton) {
        return comunicadosButton;
      }
    }

    // Se for meu perfil, minha imobiliária ou usuários, busca o menu do usuário
    if (normalizedKeyword.includes('meu perfil') ||
      normalizedKeyword.includes('minha imobiliaria') ||
      normalizedKeyword.includes('usuario')) {
      const userMenuButton = document.querySelector('user button[mat-icon-button]');
      if (userMenuButton) {
        return userMenuButton;
      }
    }

    // Procura em todos os itens de navegação lateral
    const navItems = document.querySelectorAll('.fuse-vertical-navigation-item-wrapper');

    for (const item of Array.from(navItems)) {
      const text = item.textContent?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
      if (text.includes(normalizedKeyword)) {
        return item;
      }
    }

    // Fallback: procura em links
    const links = document.querySelectorAll('fuse-vertical-navigation-basic-item a');
    for (const link of Array.from(links)) {
      const text = link.textContent?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
      if (text.includes(normalizedKeyword)) {
        return link.closest('.fuse-vertical-navigation-item-wrapper') || link;
      }
    }

    return null;
  }

  /**
   * Verifica se o elemento está na navbar superior
   */
  private isNavbarElement(): boolean {
    if (!this.highlightRect) return false;
    // Elementos na navbar ficam no topo (primeiros 80px) e à direita
    return this.highlightRect.top < 80 && this.highlightRect.left > window.innerWidth / 2;
  }

  /**
   * Posiciona o tooltip próximo ao elemento destacado
   */
  private positionTooltip(): void {
    if (!this.highlightRect) {
      this.centerTooltip();
      return;
    }

    const tooltipWidth = 380;
    const tooltipHeight = 280;
    const margin = 20;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top: number;
    let left: number;

    // Se é um elemento da navbar superior (comunicados, menu usuário)
    if (this.isNavbarElement()) {
      // Posiciona abaixo do elemento
      top = this.highlightRect.bottom + margin;

      // Centraliza horizontalmente com o elemento, mas garante que fique na tela
      left = this.highlightRect.left + (this.highlightRect.width / 2) - (tooltipWidth / 2);

      // Garante que não saia da tela pela direita
      if (left + tooltipWidth > viewportWidth - margin) {
        left = viewportWidth - tooltipWidth - margin;
      }

      // Garante que não saia pela esquerda
      if (left < margin) {
        left = margin;
      }

      this.arrowDirection = 'top';
    } else {
      // Elemento da navegação lateral - posiciona à direita
      left = this.highlightRect.right + margin;

      // Garante que não saia da tela horizontalmente
      if (left + tooltipWidth > viewportWidth - margin) {
        left = viewportWidth - tooltipWidth - margin;
      }

      // Calcula a posição vertical - tenta alinhar com o elemento
      top = this.highlightRect.top;

      // Se o tooltip ficaria cortado embaixo, move para cima
      if (top + tooltipHeight > viewportHeight - margin) {
        top = viewportHeight - tooltipHeight - margin;
      }

      // Garante que não saia pelo topo
      if (top < margin) {
        top = margin;
      }

      this.arrowDirection = 'left';
    }

    this.tooltipPosition = { top, left };
    this.cdr.detectChanges();
  }

  /**
   * Centraliza o tooltip na tela
   */
  private centerTooltip(): void {
    const tooltipWidth = 420;
    const tooltipHeight = 280;

    this.tooltipPosition = {
      top: (window.innerHeight - tooltipHeight) / 2,
      left: (window.innerWidth - tooltipWidth) / 2
    };
    this.arrowDirection = 'bottom';
    this.cdr.detectChanges();
  }

  /**
   * Remove destaque dos elementos
   */
  private removeHighlight(): void {
    document.querySelectorAll('.tour-highlight-element').forEach(el => {
      el.classList.remove('tour-highlight-element');
    });
  }

  /**
   * Avança para o próximo step
   */
  nextStep(): void {
    this.removeHighlight();

    if (this.isLastStep) {
      this.finishTour();
    } else {
      this.currentStepIndex++;
      this.showStep();
    }
  }

  /**
   * Volta para o step anterior
   */
  previousStep(): void {
    if (!this.isFirstStep) {
      this.removeHighlight();
      this.currentStepIndex--;
      this.showStep();
    }
  }

  /**
   * Pula o tour - salva no backend para não mostrar novamente
   */
  async skipTour(): Promise<void> {
    this.removeHighlight();
    this.isVisible = false;
    this.cleanupObservers();

    // Registra no backend que o onboarding foi concluído
    await this.registrarOnboardingConcluido();
  }

  /**
   * Registra no backend que o onboarding foi concluído
   */
  private async registrarOnboardingConcluido(): Promise<void> {
    try {
      const currentUser = this.authService.getUserInToken();

      if (currentUser && currentUser.userId) {
        const response = await firstValueFrom(this.usuarioService.concluirOnboarding(currentUser.userId));

        // Atualizar o token com o novo JWT retornado pelo backend
        if (response.token) {
          this.authService.setToken(new TokenVO({ token: response.token }));
        }
      }
    } catch (error) {
      console.error('Erro ao registrar onboarding concluído:', error);
    }
  }

  /**
   * Fecha o tour sem salvar preferência (vai mostrar novamente)
   */
  private closeTour(): void {
    this.removeHighlight();
    this.isVisible = false;
    this.cleanupObservers();
  }

  /**
   * Finaliza o tour (ao concluir todos os passos)
   * Marca como concluído no backend
   */
  private async finishTour(): Promise<void> {
    this.removeHighlight();
    this.isVisible = false;
    this.cleanupObservers();

    // Registra no backend que o onboarding foi concluído
    await this.registrarOnboardingConcluido();
  }

  /**
   * Configura observer de mutações no DOM
   */
  private setupMutationObserver(): void {
    this.mutationObserver = new MutationObserver(() => {
      if (this.isVisible && this.currentStep?.menuKeyword) {
        setTimeout(() => this.positionTooltip(), 100);
      }
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Configura observer de resize
   */
  private setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      if (this.isVisible && this.currentStep?.menuKeyword) {
        this.positionTooltip();
      }
    });

    this.resizeObserver.observe(document.body);
  }

  /**
   * Limpa os observers
   */
  private cleanupObservers(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  /**
   * Reseta o tour (para testes - remove do localStorage legado)
   */
  resetTour(): void {
    // Apenas para debug/testes - o controle real é via backend
    console.log('Para resetar o tour, altere onboardingConcluido no banco de dados.');
  }
}
